import React, { useEffect } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import { InfoIcon } from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { setCustomUserRole } from '../../store/general/general-slice';
import User from '../../models/general/user';
import { selectCurrentUserRole } from '../../store/general/general-slice-selectors';
import UserRole from '../../models/enums/user-roles';
import { selectCurrentOrganizationId } from '../../store/organization/organization-slice-selectors';

const RoleTopBarPopover: React.FC<{
  user: User;
  anchorEl: HTMLButtonElement | HTMLDivElement | null;
  onClose: () => void;
}> = ({ user, anchorEl, onClose }) => {
  const { t } = useTranslation();

  const maxUserRole = useAppSelector((state) => selectCurrentUserRole(state, true));
  const currentUserRole = useAppSelector((state) => selectCurrentUserRole(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (
      currentOrganizationId === user.organization.id &&
      currentUserRole === UserRole.OWNER &&
      user.role !== UserRole.OWNER
    ) {
      dispatch(setCustomUserRole(user.role));
    }
  }, [currentOrganizationId, currentUserRole, dispatch, user.organization.id, user.role]);

  const setCustomRoleClickHandler = (role: UserRole) => {
    dispatch(setCustomUserRole(role));
    onClose();
  };

  const allowedUserRoles: UserRole[] = [maxUserRole];
  if (maxUserRole === UserRole.OWNER && !allowedUserRoles.includes(UserRole.ADMIN)) {
    allowedUserRoles.push(UserRole.ADMIN);
  }
  if (
    (maxUserRole === UserRole.OWNER || maxUserRole === UserRole.ADMIN) &&
    !allowedUserRoles.includes(UserRole.MODERATOR)
  ) {
    allowedUserRoles.push(UserRole.MODERATOR);
  }
  if (!allowedUserRoles.includes(UserRole.MEMBER)) allowedUserRoles.push(UserRole.MEMBER);

  return (
    <React.Fragment>
      <Popover
        id="role-popover-id"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack>
          {allowedUserRoles.map((role) => (
            <Button
              sx={(theme) => ({
                px: 2,
                justifyContent: 'space-between',
                width: theme.spacing(24),
                color: theme.palette.action.active,
              })}
              onClick={() => setCustomRoleClickHandler(role)}
              key={role}
              size="large"
              endIcon={
                <Tooltip title={t('user-role-description-' + role)}>
                  <InfoIcon color="disabled" data-testid={'user-role-description-' + role} />
                </Tooltip>
              }
            >
              {t('user-role-' + role)}
            </Button>
          ))}
        </Stack>
      </Popover>
    </React.Fragment>
  );
};

export default RoleTopBarPopover;
